import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDownIcon } from '@heroicons/react/24/solid'
import { LANDING_FAQS } from '@/data/landingFaqData'

export default function LandingFAQ() {
  // Click-only accordion: tap/click a card to toggle its answer. No
  // hover behavior — keeps the interaction predictable across devices.
  const [openId, setOpenId] = useState(null)

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: LANDING_FAQS.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: { '@type': 'Answer', text: faq.a },
    })),
  }

  return (
    <section
      id="faq"
      aria-label="Common questions"
      className="relative overflow-hidden"
      style={{ backgroundColor: '#faf5e8' }}
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Subtle texture overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'url(/golden-light.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center bottom',
          opacity: 0.04,
          mixBlendMode: 'multiply',
        }}
      />

      <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-8 py-20 md:py-28">
        {/* Section header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
        >
          <h2
            className="font-heading text-3xl md:text-4xl font-extrabold leading-tight mb-2"
            style={{ color: '#1a0f00' }}
          >
            Common <span style={{ color: '#c9a030' }}>Questions</span>
          </h2>
        </motion.div>

        {/* Accordion — card-style FAQs. Click/tap to toggle each answer. */}
        <div className="space-y-3">
          {LANDING_FAQS.map((faq, i) => {
            const open = openId === i
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
                className="rounded-xl overflow-hidden"
                style={{
                  background: '#fffcf5',
                  border: '1px solid rgba(201,160,48,0.18)',
                  boxShadow: '0 1px 8px rgba(0,0,0,0.05)',
                }}
              >
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : i)}
                  aria-expanded={open}
                  aria-controls={`faq-panel-${i}`}
                  id={`faq-trigger-${i}`}
                  className="w-full flex items-center justify-between px-5 py-4 text-left transition-colors"
                  style={{ background: 'transparent' }}
                >
                  <span
                    className="font-semibold text-sm md:text-base pr-4 leading-snug"
                    style={{ color: '#1a0f00' }}
                  >
                    {faq.q}
                  </span>
                  <ChevronDownIcon
                    className={`w-4 h-4 shrink-0 transition-transform duration-200 ${
                      open ? 'rotate-180' : ''
                    }`}
                    style={{ color: '#c9a030' }}
                    aria-hidden="true"
                  />
                </button>
                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      id={`faq-panel-${i}`}
                      role="region"
                      aria-labelledby={`faq-trigger-${i}`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div
                        className="px-5 pb-4 text-sm md:text-base leading-relaxed"
                        style={{ color: '#3d2e1e' }}
                      >
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
